/**
 * Alpine.js Select Editor Component
 * Provides inline editing functionality for dropdown/select fields
 */

import { editorBase } from './alpine-editor-base.js';

/**
 * Select Editor Component Factory
 * Handles dropdown fields like relationship status, education level, etc.
 */
export function selectEditor() {
    return {
        // State
        isEditing: false,
        value: '',
        originalValue: '',
        isSaving: false,
        error: null,

        // Configuration (set from data attributes or defaults)
        fieldName: '',
        endpoint: '',
        options: [], // Array of {value, label} objects
        
        // Computed properties
        get hasChanges() {
            return this.value !== this.originalValue;
        },
        
        get displayValue() {
            if (!this.value) {
                return `<span class="text-gray-400 italic">Click to select ${this.fieldName}</span>`;
            }
            
            // Find the label for the current value
            const option = this.options.find(opt => opt.value === this.value);
            return editorBase.escapeHtml(option ? option.label : this.value);
        },
        
        // Methods
        init() {
            // Initialize from element data attributes
            const element = this.$el;
            
            this.fieldName = element.dataset.field || this.fieldName;
            
            // Get endpoint based on field
            this.endpoint = element.dataset.endpoint || editorBase.getEndpointForField(this.fieldName);
            
            // Parse options from data attribute
            try {
                this.options = JSON.parse(element.dataset.options || '[]');
            } catch (e) {
                console.error('Failed to parse options for', this.fieldName, e);
                this.options = [];
            }
            
            // Get initial value from element's data attributes
            let rawValue = element.dataset.rawValue || element.dataset.value || '';
            
            // If no value on element, check for .inline-edit-content child (legacy)
            if (!rawValue) {
                const contentEl = element.querySelector('.inline-edit-content');
                if (contentEl) {
                    rawValue = contentEl.dataset.rawValue || contentEl.dataset.value || '';
                }
            }
            
            // Decode HTML entities if the value came from a data attribute
            if (rawValue) {
                rawValue = editorBase.decodeHtml(rawValue);
            }
            
            // If still no value, try to get from store
            if (!rawValue) {
                rawValue = this.$store.profile?.getFieldValue(this.fieldName) || '';
            }
            
            // Validate value matches available options
            const validValues = this.options.map(opt => opt.value);
            if (rawValue && !validValues.includes(rawValue)) {
                console.warn(`Invalid select value "${rawValue}" for field "${this.fieldName}". Available options:`, validValues);
            }
            
            this.originalValue = rawValue;
            this.value = this.originalValue;
            
            // Initialize base utilities
            editorBase.initEditor(this);
        },

        startEdit() {
            // Make sure we start with the original value
            this.value = this.originalValue;
            this.isEditing = true;
            this.error = null;
            
            // Register with store for tracking
            if (this.$store.profile) {
                this.$store.profile.startEditing(this.fieldName);
            }
            
            // Focus the select after Alpine updates the DOM
            this.$nextTick(() => {
                const select = this.$refs.select;
                if (select) {
                    select.focus();
                }
            });
        },

        cancel() {
            this.value = this.originalValue;
            this.error = null;
            this.completeEdit();
        },

        completeEdit() {
            this.isEditing = false;
            
            // Unregister from store
            if (this.$store.profile) {
                this.$store.profile.stopEditing(this.fieldName);
            }
        },

        handleKeydown(event) {
            if (event.key === 'Escape') {
                this.cancel();
            } else if (event.key === 'Enter') {
                event.preventDefault();
                this.save();
            }
        },

        async save() {
            if (!this.hasChanges || this.isSaving) {
                return;
            }
            
            this.isSaving = true;
            this.error = null;
            
            try {
                // Save to server and wait for response
                const result = await editorBase.saveField(this.endpoint, this.fieldName, this.value);
                
                // Save successful - update local state
                this.originalValue = this.value;
                
                // Update store
                if (this.$store.profile) {
                    this.$store.profile.updateField(this.fieldName, this.value);
                }
                
                // Exit edit mode
                this.completeEdit();
                
            } catch (error) {
                // Show error and keep in edit mode
                this.error = error.message || 'Failed to save changes. Please try again.';
                console.error('Save error:', error);
            } finally {
                this.isSaving = false;
            }
        }
    }
}